import { InputElement } from '../schema/input.schema';
import { ToggleElement } from '../schema/toggle.schema';
import { ChoiceElement } from '../schema/choice.schema';
import { ContextElement } from '../schema/context.schema';
import { SwitchableElement, SwitchableOption } from '../schema/switchable.schema';
import { FormContainer as FormContainerType, FormElement } from '../schema/form.schema';
import { useEffect, useState } from 'react';

export type FormDataValue = string | Array<string> | boolean;

export type Data = Array<{ key: string; value: FormDataValue }>;

interface FormData {
	[key: string]: FormDataValue;
}

type LeafElement = InputElement | ToggleElement | ChoiceElement;

type NodeElement = ContextElement | SwitchableElement;

export const useFormData = (schema: FormContainerType, onChange: (data: Data) => void) => {
	const [data, setData] = useState<FormData>(getBaseState(schema));

	useEffect(() => {
		const initialData = Object.entries(data).map(([key, value]) => ({ key, value }));
		onChange(initialData);
	}, [data]);

	const onDataChange = (key: string, value: FormDataValue) => {
		let newData = { ...data, [key]: value };

		const switchableElements = schema.elements.filter(
			(element) => element.type === 'switchable'
		) as Array<SwitchableElement>;

		switchableElements.forEach((switchableElement) => {
			const selectedOption = switchableElement.options.find((option) => option.value === value);
			if (selectedOption) {
				switchableElement.options.forEach((option) => {
					if (option !== selectedOption) {
						newData = deleteKeysRecursively(option.content, newData);
					}
				});
			}
		});

		setData(newData);

		let updatedSwitchableOption: SwitchableOption | undefined;
		switchableElements.some((switchableElement) => {
			const foundOption = switchableElement.options.find((option) => option.value == value);
			if (foundOption) {
				updatedSwitchableOption = foundOption;
				return true;
			}
			return false;
		});

		if (!updatedSwitchableOption) return;
		updatedSwitchableOption.content.forEach((element) => {
			getElementData(element, newData);
		});

		onChange(
			Object.entries(newData).reduce((acc, current) => {
				const [key, value] = current;
				acc.push({ key, value });
				return acc;
			}, [] as Data)
		);
	};

	return { data, onDataChange };
};

const getBaseState = (schema: FormContainerType): FormData => {
	const data: FormData = {};
	const elements = schema.elements;
	elements.forEach((element) => {
		getElementData(element, data);
	});
	return data;
};

const getElementData = (element: FormElement, data: FormData) => {
	if (isLeafElement(element)) {
		data[element.key] = getLeafElementBaseState(element);
		return;
	}

	if (isNodeELement(element)) {
		getNodeElementData(element, data);
	}
};

const LEAF_ELEMENTS = ['input', 'choice', 'toggle'];
const NODE_ELEMENTS = ['context', 'switchable'];

export const isLeafElement = (element: FormElement): element is LeafElement => {
	return LEAF_ELEMENTS.includes(element.type);
};

const isNodeELement = (element: FormElement): element is NodeElement => {
	return NODE_ELEMENTS.includes(element.type);
};

const getLeafElementBaseState = (element: LeafElement): FormDataValue => {
	switch (element.type) {
		case 'input':
			return element.prefill_value || '';
		case 'toggle':
			return element.selected;
		case 'choice':
			return element.preselected || [];
		default:
			throw new Error(`Unsupported leaf element type: ${element}`);
	}
};

const getNodeElementData = (element: NodeElement, data: FormData) => {
	const getElementDataRecursive = (element: FormElement) => {
		getElementData(element, data);
	};

	if (element.type === 'context') {
		element.content.forEach(getElementDataRecursive);
		return;
	}

	if (element.type === 'switchable') {
		data[element.key] = element.selected;

		const selectedOptions = element.options.find((option) => option.value === element.selected);
		if (!selectedOptions) return;
		selectedOptions.content.forEach((element) => {
			getElementDataRecursive(element);
		});
	}
};

const deleteKeysRecursively = (content: Array<FormElement>, data: FormData) => {
	let newData = data;

	content.forEach((element: FormElement) => {
		if (isLeafElement(element)) {
			delete newData[element.key];
		}
		if (isNodeELement(element)) {
			if (element.type === 'context') {
				deleteKeysRecursively(element.content, data);
			} else {
				element.options.forEach((option) => {
					deleteKeysRecursively(option.content, data);
				});
			}
		}
	});
	return newData;
};
